import { add, divide, subtract } from 'tio-ui/math';
import { concat, hash } from '@ember/helper';
import Input from 'tio-ui/components/input';
import { eq, not, or } from 'tio-ui/utilities';
import { Section } from 'tio-ui/components/layout';
import { t } from 'ember-intl';
import AlertBanner from 'tio-ui/components/alert-banner';
import CalculatedApplicationTotal from '../calculated-application-total.gts';
import DefinitionList from '../../../ui/definition-list.gts';
import EditableApplicationTotal from '../editable-application-total.gts';
import inputmask from '../../../../modifiers/inputmask.ts';
import ReimbursementCalculationIfApplicable from '../../reimbursement-calculation-if-applicable.gts';
import safeFormatNumber from '../../../../helpers/safe-format-number.ts';
import Component from '@glimmer/component';
import type TasApplicationModel from 'tio-data/models/tas-application';
import type TASProgramInstanceModel from 'tio-data/models/tas-program-instance';
import { service } from '@ember/service';
import type { IntlService } from 'ember-intl';

interface RequestSummaryAndApprovedTotalSig {
  Args: {
    application: TasApplicationModel;
    programInstance: TASProgramInstanceModel;
    isCalculatedApplicationTotal: boolean;
    readonlyOriginalApplicationTotal: number;
    readonlyCourseCashTuitionTotal?: number;
    updateAndSaveApprovedTotalWithComment: (
      approvedTotal: number,
      comment: string,
      approvedCourseCashTotalField?: Record<string, unknown>
    ) => void;
    setApplicationTotal: (approvedTotal: number) => void;
    setCourseCashTotal: (courseCashTotal: number) => void;
    isProhibitedEdit: boolean;
    isPendingApprovalAndNotReadonly: boolean;
    isAllowedEditApprovedTotalIfNotPendingApproval: boolean;
    isSubmitting: boolean;
  };
  Blocks: {
    warning: [];
  };
  Element: HTMLElement;
}

class CoursesStatusApprovalRequestSummaryAndApprovedTotalComponent extends Component<RequestSummaryAndApprovedTotalSig> {
  @service declare intl: IntlService;

  get isCreditBased() {
    return this.args.application.isCreditBased;
  }

  formatCredits = (count: number) => {
    return this.intl.t('tas.dashboard.annual_benefits.credits_count', { count });
  };

  <template>
    <Section>
      <:header>
        {{t "tuition_assistance.application_details.approval_summary"}}
      </:header>
      <:body>
        <DefinitionList as |options|>
          {{#each @application.activeCourses as |course index|}}
            <div class={{options.wrappingClass}}>
              <options.dt class="truncate">
                {{or (or course.courseName course.courseNumber) (concat "Expense " (add index 1))}}
              </options.dt>
              <options.dd>
                {{#if this.isCreditBased}}
                  {{this.formatCredits course.courseCredit}}
                {{else}}
                  {{safeFormatNumber
                    (divide course.courseTotal 100)
                    style="currency"
                    currency="USD"
                  }}
                {{/if}}
              </options.dd>
            </div>
          {{/each}}
          {{#if @application.includesScholarships}}
            <div class={{options.wrappingClass}}>
              <options.dt>
                {{t "tuition_assistance.application_details.scholarships_received"}}
              </options.dt>
              <options.dd>
                {{safeFormatNumber
                  (divide (subtract 0 @application.scholarshipsTotal) 100)
                  style="currency"
                  currency="USD"
                }}
              </options.dd>
            </div>
          {{/if}}
          <div class={{options.wrappingClass}}>
            <options.dt>
              {{#if this.isCreditBased}}
                {{t "tuition_assistance.limits_summary.requested_credits"}}
              {{else}}
                {{t "tuition_assistance.application_details.requested_reimbursement"}}
              {{/if}}
            </options.dt>
            <options.dd>
              {{#if this.isCreditBased}}
                {{this.formatCredits @application.totalCredits}}
              {{else}}
                {{safeFormatNumber
                  (divide @application.requestedTotal 100)
                  style="currency"
                  currency="USD"
                }}
              {{/if}}
            </options.dd>
          </div>
          <div class={{concat options.wrappingClass " flex-col"}}>
            <ReimbursementCalculationIfApplicable @application={{@application}} />
            {{#if @application.canRequestRepayment}}
              <div class="my-2">
                <AlertBanner @intent="info">
                  <p>
                    {{t "tuition_assistance.application_details.partial_reimbursement_instruction"}}
                  </p>
                </AlertBanner>
              </div>
            {{/if}}
          </div>
          <div class={{options.wrappingClass}}>
            <options.dt>
              {{t "tuition_assistance.program_details.status.approved_total"}}
            </options.dt>
            <options.dd class="max-w-80 flex flex-col gap-y-2">
              {{#if @isCalculatedApplicationTotal}}
                <CalculatedApplicationTotal
                  @approvedTotal={{@readonlyOriginalApplicationTotal}}
                  @application={{@application}}
                  @tasProgramTemplate={{@application.tasProgramInstance.tasProgramTemplate}}
                  @onSave={{@updateAndSaveApprovedTotalWithComment}}
                  @canEdit={{not @isProhibitedEdit}}
                  @courseCashTotal={{@readonlyCourseCashTuitionTotal}}
                />
              {{else}}
                {{#if @isPendingApprovalAndNotReadonly}}
                  {{#if (eq @application.paymentMethod "TAS.AssistanceManner.COURSE_CASH")}}
                    <Input
                      @value={{String (divide (or @readonlyCourseCashTuitionTotal 0) 100)}}
                      {{! @glint-expect-error: inputmask is auto-unmasking as a number}}
                      @onChange={{@setCourseCashTotal}}
                      {{inputmask
                        alias="currency"
                        prefix="$"
                        unmaskAsNumber=true
                        digits="2"
                        autoUnmask=true
                      }}
                      disabled={{@isSubmitting}}
                      @description="Course Cash - Tuition"
                      @classes={{hash base="mb-2"}}
                    />
                  {{/if}}
                  <Input
                    @value={{String (divide @readonlyOriginalApplicationTotal 100)}}
                    {{! @glint-expect-error: inputmask is auto-unmasking as a number}}
                    @onChange={{@setApplicationTotal}}
                    {{inputmask
                      alias="currency"
                      prefix="$"
                      unmaskAsNumber=true
                      digits="2"
                      autoUnmask=true
                    }}
                    disabled={{@isSubmitting}}
                    @description={{if
                      (eq @application.paymentMethod "TAS.AssistanceManner.COURSE_CASH")
                      "All other expenses"
                      ""
                    }}
                  />
                {{else if @isAllowedEditApprovedTotalIfNotPendingApproval}}
                  <EditableApplicationTotal
                    @approvedTotal={{@readonlyOriginalApplicationTotal}}
                    @onSave={{@updateAndSaveApprovedTotalWithComment}}
                    @application={{@application}}
                    @courseCashTotal={{@readonlyCourseCashTuitionTotal}}
                  />
                {{else}}
                  <div class="font-semibold text-right">
                    {{safeFormatNumber
                      (divide @readonlyOriginalApplicationTotal 100)
                      style="currency"
                      currency="USD"
                    }}
                  </div>
                {{/if}}
              {{/if}}
            </options.dd>
          </div>
        </DefinitionList>
        {{yield to="warning"}}
      </:body>
    </Section>
  </template>
}

export default CoursesStatusApprovalRequestSummaryAndApprovedTotalComponent;
